/*
 * Copyright (c) 2023 Anvilor Solutions LLC - All Rights Reserved.
 *
 * The information and source code contained herein is the exclusive property of
 * Anvilor Solutions LLC and may not be disclosed, examined, or reproduced in whole
 * or in part without the explicit written authorization from Anvilor Solutions LLC.
 */

import { AnvilorFormsAppStore } from "../anvilor-forms-app-store.service";
import { AnvilorFormsAppStoreContext } from "../anvilor-forms-app-store.service";
import {
  LegacyRuiInstanceConfig,
  RuiInstance,
  RuiInstanceConfigV1,
  RuiStore,
} from "@anvilor/ngx-rui";
import { Observable } from "rxjs";
import { tap } from "rxjs";
import { switchMap } from "rxjs";
import { combineLatest } from "rxjs";
import { of } from "rxjs";
import { from } from "rxjs";
import { EelService } from "@anvilor/ngx-eel";
import { ResponseData } from "../../models/submission-edit";
import { AnvilorFormSubmissionUXConfig } from "../../models/anvilor-form-config";
import { immutableApplyPatches, PatchOperation } from "@anvilor/ngx-core-utils";
import { applyPatches } from "@anvilor/ngx-core-utils";

export class RuiInitializer {
  private readonly rui: RuiInstance;
  private readonly eel: EelService["eel"];
  private readonly store: AnvilorFormsAppStore;

  constructor(ctx: AnvilorFormsAppStoreContext) {
    this.rui = ctx.ruiInstance;
    this.eel = ctx.eel.eel;
    this.store = ctx.store;
  }

  initializeRui(
    anvilorForm$: Observable<AnvilorFormSubmissionUXConfig>,
    submissionData$: Observable<ResponseData | null>,
    patches$: Observable<PatchOperation[] | null>,
    autosaveData: Promise<any>
  ): Observable<any> {
    return anvilorForm$.pipe(
      tap((doc) => this.registerEelDefs(doc)),
      tap((doc) => this.initializeRuiForm(doc)),
      switchMap((anvilorFormDoc) =>
        combineLatest([
          of(anvilorFormDoc),
          submissionData$,
          patches$,
          from(autosaveData),
        ])
      ),
      tap(([form, submissionData, patches, autosavedData]) => {
        this.setInitialRuiData(
          form,
          submissionData,
          patches,
          form.display_config.enable_autosave ? autosavedData : null
        );
      })
    );
  }

  private registerEelDefs(doc: AnvilorFormSubmissionUXConfig): void {
    let eelDefs = doc.eel_definitions ?? [];
    eelDefs = eelDefs.concat(doc.display_config.eel_definitions ?? []);
    this.eel.scope.registerOperatorsForDefinitions(...eelDefs);
  }

  private initializeRuiForm(doc: AnvilorFormSubmissionUXConfig): void {
    const ruiConfig = this.getRuiConfig(doc);
    this.rui.loadConfig(ruiConfig);
  }

  private getRuiConfig(
    doc: AnvilorFormSubmissionUXConfig
  ): RuiInstanceConfigV1 | LegacyRuiInstanceConfig {
    return (
      doc.display_config.rui_config ?? {
        root_control: doc.display_config.root_control!,
        global_defaults: doc.display_config.rui_global_defaults,
        form_rules: doc.display_config.rui_form_rules,
      }
    );
  }

  private setInitialRuiData(
    anvilorFormDoc: AnvilorFormSubmissionUXConfig,
    submissionData: ResponseData | null,
    patches: PatchOperation[] | null,
    autosavedData: any | null
  ): void {
    const defaultData: any = anvilorFormDoc.display_config.default_data ?? {};
    const submittedData: any = submissionData?.response_data ?? null;
    let initialData = autosavedData ?? submittedData ?? defaultData;
    initialData = immutableApplyPatches(initialData, patches ?? []);
    this.rui.setRootData(initialData);
    this.store.patchState({ rui_initialization_status: "complete" });
  }
}
